// The scenario's risk trajectory as a line chart (Manus "Clinical state evolution graph",
// approved 2026-11-02). Points are the handoff pack's fixed demonstration values, one per stage,
// never computed. Neutral colours only: no severity band (pack acceptance test). Inline SVG, so
// no chart dependency; colours come from CSS tokens, so light and dark both work.

import type { Stage } from '../scenario/stages.ts';

/** Short stage names under the axis; Stage 5 is gated (Stage 5 GCS provenance). */
const SHORT = ['Initial', 'Early', 'Laboratory', 'Additional', 'Current'] as const;

const W = 600;
const H = 200;
const TOP = 24;
const BOTTOM = 16;
const x = (i: number, slots: number) => ((i + 0.5) / slots) * W;
const y = (risk: number) => TOP + ((100 - risk) / 100) * (H - TOP - BOTTOM);

/** How the current fixed value compares with the stage before, in neutral words. */
export function directionText(stages: readonly Stage[], index: number): string {
  const current = stages[index];
  const before = stages[index - 1];
  if (current === undefined) return '';
  if (before === undefined) return `Stage ${current.number}: starting value.`;
  const word =
    current.risk > before.risk ? 'Higher' : current.risk < before.risk ? 'Lower' : 'Unchanged';
  return word === 'Unchanged'
    ? `Unchanged from Stage ${before.number}.`
    : `${word} than Stage ${before.number}.`;
}

export function TrajectoryChart({
  stages,
  index,
  total,
  times,
}: {
  /** The built stages, in order. */
  stages: readonly Stage[];
  /** The active stage. */
  index: number;
  /** All stages, including gated ones without data. */
  total: number;
  /** Live patient page: the clinical time (HH:MM) each stage was reached. */
  times?: readonly string[];
}) {
  const reached = stages.slice(0, index + 1);
  const label =
    `Risk trajectory, fixed demonstration values: ` +
    reached.map((s) => `Stage ${s.number} ${s.risk}%`).join(', ') +
    (index + 2 === total
      ? `. Stage ${total} not reached.`
      : `. Stages ${index + 2} to ${total} not reached.`);
  const line = reached.map((s, i) => `${x(i, total)},${y(s.risk)}`).join(' ');
  return (
    <div className="traj">
      <div className="traj-head">
        <span className="output-label">Risk representation (%)</span>
        <span className="traj-event" data-testid="trajectory-stage">
          Stage {index + 1} / {total}
        </span>
      </div>
      <svg
        className="traj-chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={label}
        data-testid="trajectory-chart"
      >
        {[25, 50, 75].map((v) => (
          <g key={v}>
            <line className="traj-grid" x1={0} x2={W} y1={y(v)} y2={y(v)} />
            <text className="traj-axis" x={4} y={y(v) - 4}>
              {v}%
            </text>
          </g>
        ))}
        <line className="traj-base" x1={0} x2={W} y1={y(0)} y2={y(0)} />
        <polyline className="traj-line" points={line} />
        {Array.from({ length: total }, (_, i) => {
          const stage = stages[i];
          const cx = x(i, total);
          if (stage === undefined || i > index) {
            return (
              // Not reached: on the baseline, so its position shows no value.
              <circle key={i} className="traj-future" cx={cx} cy={y(0)} r={5} />
            );
          }
          const current = i === index;
          return (
            <g key={i} data-point={stage.number}>
              <circle
                className={`traj-point${current ? ' current' : ''}`}
                cx={cx}
                cy={y(stage.risk)}
                r={current ? 9 : 6}
              />
              <text className="traj-value" x={cx} y={y(stage.risk) - 14} textAnchor="middle">
                {stage.risk}%
              </text>
            </g>
          );
        })}
      </svg>
      <ol className="traj-labels">
        {Array.from({ length: total }, (_, i) => (
          <li key={i} className={i === index ? 'current' : i > index ? 'future' : ''}>
            Stage {i + 1}
            <span>{i < stages.length ? SHORT[i] : `${SHORT[i] ?? ''} · pending approval`}</span>
            {times?.[i] !== undefined && <span className="traj-time">{times[i]}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}
